import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import Steps from 'components/Steps';
import createStepHandler from './createStepHandler';
import StepWizardControls from 'modules/Taps/components/StepWizard/StepWizardControls';
import { NEXT_STEP, PREV_STEP } from './constants';

/**
 * @typedef WizardStep
 * @type {object}
 * @property {Function} component - React component.
 * @property {Function} [onSubmit] - submit function.
 * @property {String} [formName] - redux-form  form name.
 * @property {String} title - step title.
 * @property {String} [className] - Step component item className.
 * @property {String} key - unique step key, also is used for the router "step" param
 * @property {Boolean} [hideControls] - hide form controls
 * @property {Boolean} [hidden] - step is hidden, and won't be rendered
 */

class StepWizard extends Component {
  static getDerivedStateFromProps(nextProp, prevState) {
    const {
      steps,
      router: {
        query: { step },
      },
      steps: [firstStep] = [],
    } = nextProp;
    const currentStep = steps.find(({ key }) => key === step) || firstStep;

    if (prevState && prevState.currentStep && prevState.currentStep.key === currentStep.key) {
      return null;
    }

    return {
      ...prevState,
      currentStep,
      inTransition: false,
    };
  }

  state = {
    currentStep: null,
    inTransition: false,
  };

  constructor(props) {
    super(props);
    const {
      router: {
        query,
        query: { step },
      },
      steps,
      steps: [firstStep] = [],
    } = props;
    const currentStep = steps.find(({ key }) => key === step);

    if (!currentStep && firstStep) {
      props.updateQueryStep({ ...query, step: firstStep.key });
    }
  }

  /**
   * Handle step change
   * @param step - step to transit
   * @param direction
   * @param shouldSubmit
   * @returns {Promise<void>}
   */
  onStepChange = async (step, direction = NEXT_STEP, shouldSubmit) => {
    const {
      currentStep: { onSubmit, formName },
    } = this.state;
    const {
      updateQueryStep,
      submitForm,
      router: { query },
    } = this.props;

    if (shouldSubmit) {
      this.setState({ inTransition: true });
      // Call redux-form submit action, if form is defined and should be submitted
      if (formName && direction !== PREV_STEP) {
        submitForm(formName);
        return;
      } else if (onSubmit) {
        // Call submit if redux-form not linked
        try {
          await onSubmit();
        } catch (e) {
          this.setState({ inTransition: false });
          return;
        }
      }
    }

    this.setState({ inTransition: false });

    updateQueryStep({ ...query, step });
  };

  /**
   * Step back func to be passed to the StepComponent
   * @returns {Promise<void>}
   */
  onStepBack = async () => {
    const step = this.getStep(PREV_STEP);
    await this.onStepChange(step, PREV_STEP);
  };

  /**
   * Exit wizard handler
   */
  onExit = () => {
    const { onExit } = this.props;
    onExit && onExit();
  };

  /**
   * Go to step by index
   * @param stepIndex number
   */
  goToStep = (stepIndex) => {
    const {
      steps,
      updateQueryStep,
      router: { query },
    } = this.props;
    const currentStep = steps[stepIndex];

    updateQueryStep({ ...query, step: currentStep.key });
  };

  resetTransition = () => {
    this.setState({ inTransition: false });
  };

  /**
   * Get next step index by direction - next or prev
   * @param direction
   * @returns {String}
   */
  getStep = (direction = NEXT_STEP) => {
    const { steps } = this.props;
    const { currentStep: { key: currentStepKey } = {} } = this.state;
    const stepIndex = steps.findIndex(({ key }) => key === currentStepKey);

    return this.getStepIndex(stepIndex, direction);
  };

  /**
   * Find next or prev step considering hidden step prop
   * @param currentStepIndex
   * @param direction
   * @returns {null|String}
   */
  getStepIndex = (currentStepIndex, direction) => {
    const { steps } = this.props;
    if (direction === NEXT_STEP) {
      for (let i = currentStepIndex + 1; i < steps.length; i++) {
        if (steps[i] && !steps[i].hidden) {
          return steps[i].key;
        }
      }
    } else if (direction === PREV_STEP) {
      for (let i = currentStepIndex - 1; i >= 0; i--) {
        if (steps[i] && !steps[i].hidden) {
          return steps[i].key;
        }
      }
    }

    return null;
  };

  getPrevStep = () => {
    return this.getStep(PREV_STEP);
  };

  getNextStep = () => {
    return this.getStep(NEXT_STEP);
  };

  /**
   * Get current step component and set its props with formName,
   * prev and next step keys and step submit handlers
   * @returns {*}
   */
  renderStepComponent() {
    const {
      currentStep: { component, onSubmit, formName, hidden } = {},
      inTransition,
    } = this.state;

    const stepProps = {
      formName,
      nextStep: this.getNextStep(),
      prevStep: this.getPrevStep(),
      getNextStep: this.getNextStep,
      getPrevStep: this.getNextStep,
      isSubmitting: inTransition,
      stepSubmit: onSubmit,
      stepBack: this.onStepBack,
      resetTransition: this.resetTransition,
      hidden,
    };
    const Component = component || (() => null);

    return <Component {...stepProps} />;
  }

  render() {
    const {
      currentStep,
      currentStep: { key: currentStepKey, hideControls: hideStepControls } = {},
      inTransition,
    } = this.state;
    const { steps: originalSteps, hideControls, disableStepsNavigation } = this.props;
    const steps = originalSteps.filter(({ hidden }) => !hidden);
    return (
      <div>
        <Steps
          activeStepIndex={steps.findIndex(({ key }) => key === currentStepKey)}
          stepsMap={steps}
          createStepClickHandler={createStepHandler}
          goToStep={!disableStepsNavigation ? this.goToStep : undefined}
        />
        <div>{this.renderStepComponent()}</div>
        {!hideStepControls && !hideControls && (
          <StepWizardControls
            steps={steps}
            currentStep={currentStep}
            onStepChange={this.onStepChange}
            onExit={this.onExit}
            inTransition={inTransition}
          />
        )}
      </div>
    );
  }
}

StepWizard.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func]).isRequired,
        formName: PropTypes.string.isRequired,
        hidden: PropTypes.bool,
      }),
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func]).isRequired,
        onSubmit: PropTypes.func,
        hidden: PropTypes.bool,
      }),
    ]),
  ).isRequired,
  router: PropTypes.shape({
    query: PropTypes.shape({
      step: PropTypes.string,
    }),
  }),
  updateQueryStep: PropTypes.func,
  onExit: PropTypes.func,
  submitForm: PropTypes.func.isRequired,
  hideControls: PropTypes.bool,
  disableStepsNavigation: PropTypes.bool,
};

export default StepWizard;
